import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/appStore';
import {
  Card,
  CardContent,
  Badge,
  Button,
  Input,
} from '../components/ui';
import {
  GitBranch,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Copy,
  Archive,
  Trash2,
  Play,
  Eye,
  Upload,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { motion } from 'framer-motion';
import type { Flow } from '../types';
import { isPartnerReadOnlyMode } from '../lib/readOnlyMode';
import { toast } from '../stores/toastStore';
import { api, type FlowImportSummary } from '../lib/api';

export function Flows() {
  const {
    flows,
    createFlow,
    updateFlow,
    duplicateFlow,
    archiveFlow,
    deleteFlow,
    publishFlow,
    isLoading,
  } = useAppStore();

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFlowName, setNewFlowName] = useState('');
  const [newFlowDescription, setNewFlowDescription] = useState('');
  const [isCreatingFlow, setIsCreatingFlow] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [flowToRename, setFlowToRename] = useState<Flow | null>(null);
  const [renameFlowName, setRenameFlowName] = useState('');
  const [isRenamingFlow, setIsRenamingFlow] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importPreview, setImportPreview] = useState<FlowImportSummary | null>(null);
  const [importPayload, setImportPayload] = useState<unknown>(null);
  const [importName, setImportName] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const isReadOnlyMode = isPartnerReadOnlyMode();

  // Data is fetched in App.tsx


  const filteredFlows = flows.filter(
    (flow) =>
      flow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      flow.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateFlow = async () => {
    if (isReadOnlyMode) return;
    if (!newFlowName.trim() || isCreatingFlow) return;

    setIsCreatingFlow(true);
    try {
      const flow = await createFlow({
        name: newFlowName,
        description: newFlowDescription,
      });

      setShowCreateModal(false);
      setNewFlowName('');
      setNewFlowDescription('');
      navigate(`/flows/${flow.id}`);
    } finally {
      setIsCreatingFlow(false);
    }
  };

  const handleFileChosen = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // permite reescolher o mesmo arquivo
    if (!file) return;

    try {
      const text = await file.text();

      let payload: unknown;
      try {
        payload = JSON.parse(text);
      } catch {
        toast.error('O arquivo não é um JSON válido.', 'Importar fluxo', 9000);
        return;
      }

      // The server does the real validation — it checks our signature and the
      // whole schema. The browser can be bypassed, so it never decides this.
      const summary = await api.previewFlowImport(payload);
      setImportPayload(payload);
      setImportPreview(summary);
      setImportName(summary.name);
    } catch (error: any) {
      toast.error(error?.message || 'Não foi possível ler o arquivo.', 'Importar fluxo', 9000);
    }
  };

  const handleConfirmImport = async () => {
    if (!importPreview || !importPayload || isImporting) return;

    const name = importName.trim();
    if (!name) {
      toast.error('Informe um nome para o fluxo.');
      return;
    }

    setIsImporting(true);
    try {
      const result = await api.importFlow(importPayload, name);

      toast.success(
        `${result.summary.nodeCount} passos e ${result.summary.edgeCount} ligações importados.`,
        'Fluxo importado',
        6000,
      );
      setImportPreview(null);
      setImportPayload(null);
      navigate(`/flows/${result.flow.id}`);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || 'Falha ao importar o fluxo.', 'Importar fluxo', 9000);
    } finally {
      setIsImporting(false);
    }
  };

  const handleDuplicate = async (flow: Flow) => {
    if (isReadOnlyMode) return;
    const newFlow = await duplicateFlow(flow.id, `${flow.name} (Cópia)`);
    navigate(`/flows/${newFlow.id}`);
  };

  const openRenameModal = (flow: Flow) => {
    if (isReadOnlyMode) return;
    setFlowToRename(flow);
    setRenameFlowName(flow.name);
    setActiveMenu(null);
  };

  const handleRenameFlow = async () => {
    if (isReadOnlyMode) return;
    if (!flowToRename || isRenamingFlow) return;

    const nextName = renameFlowName.trim();
    if (!nextName) {
      toast.error('Informe um nome para o fluxo.');
      return;
    }

    if (nextName === flowToRename.name) {
      setFlowToRename(null);
      return;
    }

    setIsRenamingFlow(true);
    try {
      await updateFlow(flowToRename.id, { name: nextName });
      toast.success('Nome do fluxo atualizado com sucesso!');
      setFlowToRename(null);
      setRenameFlowName('');
    } catch (error: any) {
      console.error('Failed to rename flow:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Erro ao renomear fluxo.');
    } finally {
      setIsRenamingFlow(false);
    }
  };

  const getStatusBadge = (status: Flow['status']) => {
    const config = {
      draft: { variant: 'warning' as const, label: 'Rascunho' },
      published: { variant: 'success' as const, label: 'Publicado' },
      archived: { variant: 'default' as const, label: 'Arquivado' },
    };
    const { variant, label } = config[status];
    return <Badge variant={variant}>{label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-28 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-4 w-72 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
          <div className="h-10 w-32 bg-slate-200 dark:bg-slate-700 rounded-lg" />
        </div>

        <div className="h-10 w-full max-w-md bg-slate-200 dark:bg-slate-700 rounded-lg" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-slate-200 dark:bg-slate-700 rounded-lg" />
                  <div className="space-y-2">
                    <div className="h-5 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                    <div className="h-4 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
                  </div>
                </div>
                <div className="h-5 w-5 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
              <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded" />
              <div className="pt-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <div className="h-6 w-20 bg-slate-200 dark:bg-slate-700 rounded-full" />
                <div className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: 'easeOut' }}
      className="space-y-6"
    >
      {isReadOnlyMode && (
        <Card className="bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30">
          <CardContent className="py-3 text-sm text-amber-900 dark:text-amber-200">
            Modo leitura ativo: criação e edição de fluxos estão desabilitadas neste tenant.
          </CardContent>
        </Card>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Fluxos</h1>
          <p className="text-slate-500 dark:text-slate-300 mt-1">
            Crie e gerencie fluxos de conversa para seus bots
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleFileChosen}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isReadOnlyMode}
            title="Importar um fluxo exportado (.json)"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importar
          </Button>
          <Button onClick={() => setShowCreateModal(true)} disabled={isReadOnlyMode}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Fluxo
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Buscar fluxos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Flows Grid */}
      {filteredFlows.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <GitBranch className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                {searchTerm ? 'Nenhum fluxo encontrado' : 'Nenhum fluxo criado'}
              </h3>
              <p className="text-slate-500 dark:text-slate-300 mb-4">
                {searchTerm
                  ? 'Tente buscar com outros termos'
                  : 'Crie seu primeiro fluxo para automatizar conversas'}
              </p>
              {!searchTerm && (
                <Button onClick={() => setShowCreateModal(true)} disabled={isReadOnlyMode}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Fluxo
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFlows.map((flow, index) => (
            <motion.div
              key={flow.id}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.24, delay: index * 0.04, ease: 'easeOut' }}
            >
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <GitBranch className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">{flow.name}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-300">
                          v{flow.version} • {flow.nodes?.length || 0} nós
                        </p>
                      </div>
                    </div>
                    <div className="relative">
                      <button
                        onClick={() =>
                          setActiveMenu(activeMenu === flow.id ? null : flow.id)
                        }
                        disabled={isReadOnlyMode}
                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                      >
                        <MoreVertical className="w-5 h-5 text-slate-400 dark:text-slate-300" />
                      </button>
                      {activeMenu === flow.id && (
                        <div className="absolute right-0 top-8 w-48 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-10">
                          <Link
                            to={`/flows/${flow.id}`}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <Edit className="w-4 h-4" />
                            Editar
                          </Link>
                          <button
                            onClick={() => openRenameModal(flow)}
                            disabled={isReadOnlyMode}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 w-full"
                          >
                            <Edit className="w-4 h-4" />
                            Renomear
                          </button>
                          <button
                            onClick={() => handleDuplicate(flow)}
                            disabled={isReadOnlyMode}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 w-full"
                          >
                            <Copy className="w-4 h-4" />
                            Duplicar
                          </button>
                          {flow.status === 'draft' && (
                            <button
                              onClick={() => {
                                publishFlow(flow.id);
                                setActiveMenu(null);
                              }}
                              disabled={isReadOnlyMode}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-green-700 hover:bg-green-50 w-full"
                            >
                              <Play className="w-4 h-4" />
                              Publicar
                            </button>
                          )}
                          {flow.status !== 'archived' && (
                            <button
                              onClick={() => {
                                archiveFlow(flow.id);
                                setActiveMenu(null);
                              }}
                              disabled={isReadOnlyMode}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 w-full"
                            >
                              <Archive className="w-4 h-4" />
                              Arquivar
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (
                                confirm(
                                  'Tem certeza que deseja excluir este fluxo? Esta ação não pode ser desfeita.'
                                )
                              ) {
                                deleteFlow(flow.id);
                              }
                              setActiveMenu(null);
                            }}
                            disabled={isReadOnlyMode}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-red-700 hover:bg-red-50 w-full"
                          >
                            <Trash2 className="w-4 h-4" />
                            Excluir
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {flow.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 line-clamp-2">
                      {flow.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-700">
                    {getStatusBadge(flow.status)}
                    <Link to={`/flows/${flow.id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        Abrir
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Flow Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                Criar Novo Fluxo
              </h2>
              <div className="space-y-4">
                <Input
                  label="Nome do Fluxo"
                  placeholder="Ex: Atendimento Inicial"
                  value={newFlowName}
                  onChange={(e) => setNewFlowName(e.target.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                    Descrição (opcional)
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Descreva o objetivo deste fluxo..."
                    value={newFlowDescription}
                    onChange={(e) => setNewFlowDescription(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={isCreatingFlow}
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewFlowName('');
                    setNewFlowDescription('');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleCreateFlow}
                  disabled={isReadOnlyMode || !newFlowName.trim() || isCreatingFlow}
                >
                  {isCreatingFlow ? 'Criando...' : 'Criar Fluxo'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rename Flow Modal */}
      {flowToRename && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                Renomear Fluxo
              </h2>

              <div className="space-y-4">
                <Input
                  label="Novo nome do fluxo"
                  placeholder="Ex: Atendimento Inicial"
                  value={renameFlowName}
                  onChange={(e) => setRenameFlowName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleRenameFlow();
                    }
                  }}
                  autoFocus
                />
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={isRenamingFlow}
                  onClick={() => {
                    setFlowToRename(null);
                    setRenameFlowName('');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  disabled={isReadOnlyMode || !renameFlowName.trim() || isRenamingFlow}
                  onClick={() => void handleRenameFlow()}
                >
                  {isRenamingFlow ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Flow Modal */}
      {importPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Importar fluxo
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
                {importPreview.nodeCount} passos e {importPreview.edgeCount} ligações ·{' '}
                {importPreview.executionMode === 'continuous'
                  ? 'conversa livre'
                  : 'passo a passo'}
              </p>

              <div className="mt-4">
                <Input
                  label="Nome do fluxo"
                  value={importName}
                  onChange={(e) => setImportName(e.target.value)}
                />
              </div>

              {importPreview.issues.length > 0 && (
                <div className="mt-4 max-h-56 overflow-y-auto space-y-2">
                  {importPreview.issues.map((issue, index) => (
                    <div
                      key={index}
                      className={`flex gap-2 rounded-lg border p-2.5 text-xs leading-relaxed ${
                        issue.level === 'error'
                          ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100'
                          : 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300'
                      }`}
                    >
                      {issue.level === 'error' ? (
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                      ) : (
                        <Info className="w-4 h-4 shrink-0 mt-px" />
                      )}
                      <span>{issue.message}</span>
                    </div>
                  ))}
                </div>
              )}

              <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                O fluxo entra como rascunho, com novos identificadores. Nada do que já existe é alterado.
              </p>

              <div className="flex gap-3 mt-5">
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={isImporting}
                  onClick={() => { setImportPreview(null); setImportPayload(null); }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleConfirmImport}
                  disabled={isReadOnlyMode || !importName.trim() || isImporting}
                >
                  {isImporting ? 'Importando...' : 'Importar fluxo'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Click outside to close menu */}
      {activeMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActiveMenu(null)}
        />
      )}
    </motion.div>
  );
}
